import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/auth/AuthProvider';
import { atualizarCampoConfig, ouvirClinica } from '@/services/clinicasService';
import { buscarFrasesFrequentes } from '@/services/captacaoService';
import {
  MINIMO_PALAVRAS_FRASE,
  REGRAS_PADRAO,
  frasesContidas,
  frasesDoTexto,
  frasesRepetidas,
  motivoFraseInvalida,
  normalizarFrase,
  palavrasDoTexto,
  procedimentosDaClinica,
  regraValida,
} from '@/leads/inferencia';
import { chaveLigada, origemPorId, origensAtivas } from '@/leads/origens';

/**
 * Captação: as regras que dão ORIGEM, NOME DO ANÚNCIO e PROCEDIMENTO ao lead pela PRIMEIRA mensagem.
 *
 * A regra vive em `clinicas/{id}.config.inferencia` (só o gestor escreve — rules).
 *
 * ★★ PC-7 (25/out/2026) — a aba passou a ser de FRASE PRONTA. O dono: *"não vai mais ficar gerando
 * aquele link para anúncios. A gente não usa aqueles links. Vai ser digitada a frase e a origem, e o
 * anúncio."* Por regra: Frases prontas (várias, uma por linha), Origem, Nome do anúncio, Procedimento
 * (opcional) e Palavras (só procedimento — D1). SAÍRAM o gerador de link `wa.me`, o "Texto do link"
 * e o Código `#XX` (zero uso medido em 60 dias).
 *
 * ⚠ O que acontece com o que já estava gravado: `codigo` e `textoLink` são ignorados pelo servidor
 * e somem do documento no primeiro Salvar desta aba (ela regrava a lista sem eles). O texto do link
 * antigo aparece uma vez na regra, com um botão para virar frase, para não se perder em silêncio.
 *
 * Casamento (o MESMO `casarCaptacao` do servidor, espelhado): a 1ª mensagem CONTÉM a frase, sem
 * acento e sem pontuação; havendo várias, vence a MAIS LONGA. Por isso a aba avisa quando uma frase
 * está dentro de outra, e recusa frase repetida, curta ou só de cumprimento (D2).
 */

const novaLinha = () => ({ frasesTexto: '', origem: '', anuncio: '', procedimento: '', palavrasTexto: '', textoLinkAntigo: '' });

/** Do documento para o formulário: frases uma por linha, palavras por vírgula. */
const paraFormulario = (regras) =>
  (regras ?? []).map((r) => {
    const frases = Array.isArray(r.frases) ? r.frases : [];
    const textoLink = typeof r.textoLink === 'string' ? r.textoLink.trim() : '';
    return {
      frasesTexto: frases.join('\n'),
      origem: typeof r.origem === 'string' ? r.origem : '',
      anuncio: r.anuncio ?? '',
      procedimento: r.procedimento ?? '',
      palavrasTexto: (r.palavras ?? []).join(', '),
      // ★ só oferece o texto antigo quando ele ainda não virou frase desta regra
      textoLinkAntigo: textoLink && !frases.some((f) => normalizarFrase(f) === normalizarFrase(textoLink)) ? textoLink : '',
    };
  });

/** Do formulário para o documento: só o que o servidor lê, limpo. ★ Sem `codigo` e sem `textoLink`. */
const paraDocumento = (linhas) =>
  linhas.map((l) => ({
    frases: frasesDoTexto(l.frasesTexto),
    origem: String(l.origem ?? '').trim(),
    anuncio: String(l.anuncio ?? '').trim() || null,
    procedimento: String(l.procedimento ?? '').trim(),
    palavras: palavrasDoTexto(l.palavrasTexto),
  }));

const MOTIVO = {
  vazia: 'está vazia',
  curta: `é curta demais: use ${MINIMO_PALAVRAS_FRASE} palavras ou mais`,
  cumprimento: 'é só cumprimento — qualquer pessoa manda isso, não só quem viu o anúncio',
};

/** O primeiro problema que impede salvar, ou null. Mesma régua do servidor (`regraValida`/`fraseValida`). */
function problemaDasRegras(documento) {
  for (const [i, r] of documento.entries()) {
    for (const f of r.frases) {
      const motivo = motivoFraseInvalida(f);
      if (motivo) return `A frase “${f}” (regra ${i + 1}) ${MOTIVO[motivo]}.`;
    }
    if (!regraValida(r)) return `A regra ${i + 1} precisa de uma frase pronta, ou de palavras com procedimento.`;
    if (r.frases.length && !r.origem) return `A regra ${i + 1} tem frase pronta e precisa de uma origem.`;
    if (r.frases.length && !r.anuncio) return `A regra ${i + 1} tem frase pronta e precisa do nome do anúncio.`;
  }
  const repetidas = frasesRepetidas(documento);
  if (repetidas.length) {
    return `Frase repetida: “${repetidas[0]}”. Cada frase pode estar em uma regra só — senão quem decide é a ordem.`;
  }
  return null;
}

export default function SecaoCaptacao() {
  const { clinicaId } = useAuth();
  const [clinica, setClinica] = useState(null);
  const [linhas, setLinhas] = useState(null); // null = ainda não carregou
  const [inferirOrigem, setInferirOrigem] = useState(false); // a CHAVE (VH) — hoje só palavra solta
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState(null);
  const [aviso, setAviso] = useState(null);
  const [copiado, setCopiado] = useState(null);
  const [frequentes, setFrequentes] = useState(null); // null = não buscou
  const [buscando, setBuscando] = useState(false);
  const [erroFrequentes, setErroFrequentes] = useState(null);

  useEffect(() => {
    if (!clinicaId) return undefined;
    return ouvirClinica(clinicaId, setClinica, () => setErro('Não foi possível carregar a clínica.'));
  }, [clinicaId]);

  // Carrega o formulário UMA vez a partir do documento; depois quem manda é a
  // edição local — um snapshot no meio da digitação apagaria o que a pessoa escreveu.
  useEffect(() => {
    if (clinica && linhas === null) {
      setLinhas(paraFormulario(clinica.config?.inferencia));
      setInferirOrigem(chaveLigada(clinica.config));
    }
  }, [clinica, linhas]);

  const procedimentos = procedimentosDaClinica(clinica?.config);
  const origens = origensAtivas(clinica?.config);

  const documento = useMemo(() => paraDocumento(linhas ?? []), [linhas]);
  const usandoPadrao = documento.filter(regraValida).length === 0;
  const contidas = useMemo(() => frasesContidas(documento), [documento]);
  // frase normalizada → número da regra (base 1), para a lista de frequentes dizer "já cadastrada"
  const ondeEsta = useMemo(() => {
    const m = new Map();
    documento.forEach((r, i) => r.frases.forEach((f) => m.set(normalizarFrase(f), i + 1)));
    return m;
  }, [documento]);

  function mudar(i, campo, valor) {
    setLinhas((atuais) => atuais.map((l, j) => (j === i ? { ...l, [campo]: valor } : l)));
  }
  function mover(i, delta) {
    setLinhas((atuais) => {
      const j = i + delta;
      if (j < 0 || j >= atuais.length) return atuais;
      const copia = [...atuais];
      [copia[i], copia[j]] = [copia[j], copia[i]];
      return copia;
    });
  }
  const remover = (i) => setLinhas((atuais) => atuais.filter((_, j) => j !== i));
  const adicionar = () => setLinhas((atuais) => [...(atuais ?? []), novaLinha()]);
  const comecarPeloPadrao = () => setLinhas(paraFormulario(REGRAS_PADRAO));
  const usarTextoAntigo = (i) =>
    setLinhas((atuais) => atuais.map((l, j) => (j === i
      ? { ...l, frasesTexto: [l.frasesTexto.trim(), l.textoLinkAntigo].filter(Boolean).join('\n'), textoLinkAntigo: '' }
      : l)));

  async function salvar() {
    setErro(null);
    setAviso(null);
    const problema = problemaDasRegras(documento);
    if (problema) {
      setErro(problema);
      return;
    }
    setSalvando(true);
    try {
      await atualizarCampoConfig(clinicaId, 'inferencia', documento);
      await atualizarCampoConfig(clinicaId, 'inferirOrigem', inferirOrigem === true);
      setLinhas(paraFormulario(documento));
      setAviso(documento.length ? 'Regras salvas.' : 'Sem regras próprias: valem as regras padrão.');
    } catch (e) {
      console.error('[OdontoSDR] Falha ao salvar as regras de captação:', e);
      setErro(
        e?.code === 'permission-denied'
          ? 'Só o gestor pode alterar as regras de captação.'
          : 'Não foi possível salvar as regras.',
      );
    } finally {
      setSalvando(false);
    }
  }

  async function buscarFrequentes() {
    setErroFrequentes(null);
    setBuscando(true);
    try {
      setFrequentes(await buscarFrasesFrequentes(clinicaId));
    } catch (e) {
      setErroFrequentes(
        e?.code === 'permission-denied' || e?.code === 'functions/permission-denied'
          ? 'Só o gestor pode ver as frases que chegaram.'
          : 'Não foi possível buscar as frases.',
      );
    } finally {
      setBuscando(false);
    }
  }

  async function copiar(texto, chave) {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(chave);
      setTimeout(() => setCopiado(null), 1500);
    } catch {
      // Sem clipboard (http, permissão): o texto da frase é selecionável na lista.
      setErroFrequentes('Não foi possível copiar automaticamente. Selecione a frase e copie.');
    }
  }

  if (linhas === null) return <p className="vazio">Carregando as regras…</p>;

  return (
    <section className="secao">
      <header className="secao-barra">
        <div>
          <h2 className="secao-titulo">Captação — origem, anúncio e tratamento pela frase pronta</h2>
          <p className="secao-sub">
            Quando a primeira mensagem de um lead novo <strong>contém a frase pronta</strong> de um
            anúncio, o lead já nasce com a <strong>origem</strong>, o <strong>nome do anúncio</strong> e
            o procedimento da regra. Maiúsculas, acentos e pontuação não contam. Se a mensagem contém
            duas frases cadastradas, <strong>vale a mais longa</strong>. O texto da mensagem não é
            guardado no lead — só o resultado.
          </p>
          <p className="secao-sub">
            O anúncio da Meta, quando vem identificado, continua mandando na origem; a frase ainda dá
            o procedimento e o nome. Lead que já existia não muda de origem por causa de mensagem.
          </p>
          <p className="campo-ajuda">
            Use <strong>uma frase diferente em cada canal</strong>: Meta, Google, Meu Negócio e site.
            Se o texto do Google for igual ao da Meta, os dois caem na mesma origem. Copie a frase da
            lista abaixo em vez de redigitar — uma letra diferente e ela não casa.
          </p>
          <label className="campo-inline" htmlFor="captacao-inferir-origem">
            <input
              id="captacao-inferir-origem"
              type="checkbox"
              checked={inferirOrigem}
              onChange={(e) => setInferirOrigem(e.target.checked)}
            />
            Inferir a origem também por palavras soltas
          </label>
          <p className="campo-ajuda">
            A frase pronta dá a origem com esta chave ligada ou desligada. A chave liga só as palavras
            soltas da lista de origens (ex.: &ldquo;redes sociais&rdquo; → Meta Ads). As palavras das
            regras abaixo dão só o procedimento, nunca a origem nem o nome do anúncio.
          </p>
        </div>
      </header>

      {erro && <p className="erro">{erro}</p>}
      {aviso && <p className="indicador-nota">{aviso}</p>}

      {usandoPadrao && (
        <div className="cartao-anuncio">
          <span className="rotulo-acoes">Sem regras próprias — valem as regras padrão (só procedimento)</span>
          <ul className="lista-compacta">
            {REGRAS_PADRAO.map((r) => (
              <li key={r.procedimento}>
                <strong>{r.procedimento}</strong>: {r.palavras.join(', ')}
              </li>
            ))}
          </ul>
          {linhas.length === 0 && (
            <button type="button" className="btn-secundario compacto" onClick={comecarPeloPadrao}>
              Começar pelas regras padrão
            </button>
          )}
        </div>
      )}

      {contidas.length > 0 && (
        <div className="cartao-anuncio" data-teste="aviso-contidas">
          {contidas.map((c) => (
            <p key={`${c.curta}|${c.longa}`} className="campo-ajuda">
              ⚠ A frase “{c.curta}” (regra {c.regraCurta + 1}) está dentro de “{c.longa}” (regra{' '}
              {c.regraLonga + 1}). Quem mandar a mais longa cai na regra {c.regraLonga + 1}; a mais
              curta, na regra {c.regraCurta + 1}.
            </p>
          ))}
        </div>
      )}

      <div className="tabela-envolve">
        <table className="tabela tabela-captacao">
          <thead>
            <tr>
              <th>#</th>
              <th>Frases prontas (uma por linha)</th>
              <th>Origem</th>
              <th>Nome do anúncio</th>
              <th>Procedimento (opcional)</th>
              <th>Palavras — só procedimento (opcional)</th>
              <th aria-label="Ações" />
            </tr>
          </thead>
          <tbody>
            {linhas.map((l, i) => (
              <tr key={i}>
                <td>{i + 1}</td>
                <td>
                  <textarea
                    rows={2}
                    value={l.frasesTexto}
                    onChange={(e) => mudar(i, 'frasesTexto', e.target.value)}
                    placeholder="Olá, vim do Google"
                    aria-label={`Frases da regra ${i + 1}`}
                  />
                  {l.textoLinkAntigo && (
                    <p className="campo-ajuda">
                      Texto do link antigo: “{l.textoLinkAntigo}” — será descartado ao salvar.{' '}
                      <button type="button" className="btn-texto" onClick={() => usarTextoAntigo(i)}>
                        Usar como frase
                      </button>
                    </p>
                  )}
                </td>
                <td>
                  <select
                    value={l.origem}
                    onChange={(e) => mudar(i, 'origem', e.target.value)}
                    aria-label={`Origem da regra ${i + 1}`}
                  >
                    <option value="">— sem origem —</option>
                    {l.origem && !origens.some((o) => o.id === l.origem) && (
                      <option value={l.origem}>{origemPorId(clinica?.config, l.origem).label}</option>
                    )}
                    {origens.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    value={l.anuncio}
                    onChange={(e) => mudar(i, 'anuncio', e.target.value)}
                    placeholder="Implante (redes sociais)"
                    aria-label={`Anúncio da regra ${i + 1}`}
                  />
                </td>
                <td>
                  <select
                    value={l.procedimento}
                    onChange={(e) => mudar(i, 'procedimento', e.target.value)}
                    aria-label={`Procedimento da regra ${i + 1}`}
                  >
                    <option value="">— nenhum —</option>
                    {l.procedimento && !procedimentos.includes(l.procedimento) && (
                      <option value={l.procedimento}>{l.procedimento}</option>
                    )}
                    {procedimentos.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    value={l.palavrasTexto}
                    onChange={(e) => mudar(i, 'palavrasTexto', e.target.value)}
                    placeholder="implante, implantes"
                    aria-label={`Palavras da regra ${i + 1}`}
                  />
                </td>
                <td className="celula-acoes">
                  <button type="button" className="btn-texto" onClick={() => mover(i, -1)} disabled={i === 0} title="Subir (mais prioridade)">↑</button>
                  <button type="button" className="btn-texto" onClick={() => mover(i, 1)} disabled={i === linhas.length - 1} title="Descer">↓</button>
                  <button type="button" className="btn-texto perigo" onClick={() => remover(i)} title="Remover regra">×</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="dialogo-acoes">
        <button type="button" className="btn-secundario" onClick={adicionar}>
          Adicionar regra
        </button>
        <button type="button" className="btn-primario" onClick={salvar} disabled={salvando}>
          {salvando ? 'Salvando…' : 'Salvar regras'}
        </button>
      </div>

      <h3 className="secao-titulo">Frases que mais chegaram (últimos 60 dias)</h3>
      <p className="secao-sub">
        As primeiras mensagens que chegaram iguais de 5 leads ou mais. Mensagem que só uma pessoa
        mandou não aparece aqui. Copie a frase do anúncio e cole na regra.
      </p>
      <button type="button" className="btn-secundario compacto" onClick={buscarFrequentes} disabled={buscando}>
        {buscando ? 'Buscando…' : 'Buscar frases'}
      </button>
      {erroFrequentes && <p className="erro">{erroFrequentes}</p>}
      {frequentes && frequentes.frases.length === 0 && (
        <p className="vazio">Nenhuma frase chegou de {frequentes.minimo} leads ou mais nos últimos {frequentes.janelaDias} dias.</p>
      )}
      {frequentes && frequentes.frases.length > 0 && (
        <ul className="lista-links" data-teste="frases-frequentes">
          {frequentes.frases.map((f) => {
            const regra = ondeEsta.get(f.frase);
            return (
              <li key={f.frase}>
                <div>
                  <span className="frase-frequente">{f.frase}</span>
                  <span className="indicador-nota">
                    {' · '}{f.leads} leads
                    {regra ? ` · já cadastrada na regra ${regra}` : ''}
                    {f.motivo === 'cumprimento' ? ' · cumprimento — não serve como frase' : ''}
                    {f.motivo === 'curta' ? ' · curta demais para ser frase' : ''}
                  </span>
                </div>
                {!f.motivo && (
                  <button type="button" className="btn-secundario compacto" onClick={() => copiar(f.frase, f.frase)}>
                    {copiado === f.frase ? 'Copiado!' : 'Copiar'}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
